import { Component, Input, ChangeDetectionStrategy, booleanAttribute } from '@angular/core';
import { CommonModule } from '@angular/common';

export type DsFondoValue = 'A' | 'B' | 'C' | 'D' | 'E';
export type DsFondoSize = 's' | 'm' | 'l';
export type DsFondoOrientation = 'horizontal' | 'vertical';

const FONDO_DESCRIPTIONS: Record<DsFondoValue, string> = {
  A: 'Mas Riesgoso',
  B: 'Riesgoso',
  C: 'Intermedio',
  D: 'Intermedio',
  E: 'Mas conservador',
};

@Component({
  selector: 'ds-fondo',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="ds-fondo" [class]="hostClasses">
      <span class="ds-fondo__item ds-text--visual-padding-none">{{ fondo }}</span>
      <span *ngIf="showDescription" class="ds-fondo__description ds-text--visual-padding-none">
        {{ resolvedDescription }}
      </span>
    </span>
  `,
  styles: [`
    :host { display: contents; }

    .ds-fondo {
      display: inline-flex;
      align-items: center;
      gap: var(--ds-fondo-gap);
      color: var(--ds-fondo-description-color);
      font-family: 'Sura Sans', sans-serif;
      font-weight: var(--ds-fondo-font-weight);
    }

    .ds-fondo--vertical {
      flex-direction: column;
      justify-content: center;
      text-align: center;
    }

    .ds-fondo__item {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      width: var(--ds-fondo-item-size);
      height: var(--ds-fondo-item-size);
      border: var(--ds-fondo-item-border-width) solid var(--ds-fondo-item-border-color);
      border-radius: var(--ds-fondo-item-border-radius);
      background: var(--ds-fondo-item-background);
      color: var(--ds-fondo-item-foreground);
      font-size: var(--ds-fondo-item-font-size);
      line-height: var(--ds-fondo-item-line-height);
      white-space: nowrap;
    }

    .ds-fondo__description {
      min-width: 0;
      color: var(--ds-fondo-description-color);
      font-size: var(--ds-fondo-description-font-size);
      line-height: var(--ds-fondo-description-line-height);
      overflow-wrap: anywhere;
    }

    .ds-fondo--horizontal .ds-fondo__description {
      text-align: left;
    }

    .ds-fondo--vertical .ds-fondo__description {
      text-align: center;
    }

    .ds-text--visual-padding-none {
      text-box-trim: trim-both;
    }

    .ds-fondo--A { --ds-fondo-item-background: var(--ds-fondo-a-background); }
    .ds-fondo--B { --ds-fondo-item-background: var(--ds-fondo-b-background); }
    .ds-fondo--C { --ds-fondo-item-background: var(--ds-fondo-c-background); }
    .ds-fondo--D { --ds-fondo-item-background: var(--ds-fondo-d-background); }
    .ds-fondo--E { --ds-fondo-item-background: var(--ds-fondo-e-background); }

    .ds-fondo--s {
      --ds-fondo-item-size: var(--ds-fondo-s-item-size);
      --ds-fondo-item-border-radius: var(--ds-fondo-s-item-border-radius);
      --ds-fondo-item-font-size: var(--ds-fondo-s-item-font-size);
      --ds-fondo-item-line-height: var(--ds-fondo-s-item-line-height);
      --ds-fondo-description-font-size: var(--ds-fondo-s-description-font-size);
      --ds-fondo-description-line-height: var(--ds-fondo-s-description-line-height);
    }

    .ds-fondo--m {
      --ds-fondo-item-size: var(--ds-fondo-m-item-size);
      --ds-fondo-item-border-radius: var(--ds-fondo-m-item-border-radius);
      --ds-fondo-item-font-size: var(--ds-fondo-m-item-font-size);
      --ds-fondo-item-line-height: var(--ds-fondo-m-item-line-height);
      --ds-fondo-description-font-size: var(--ds-fondo-m-description-font-size);
      --ds-fondo-description-line-height: var(--ds-fondo-m-description-line-height);
    }

    .ds-fondo--l {
      --ds-fondo-item-size: var(--ds-fondo-l-item-size);
      --ds-fondo-item-border-radius: var(--ds-fondo-l-item-border-radius);
      --ds-fondo-item-font-size: var(--ds-fondo-l-item-font-size);
      --ds-fondo-item-line-height: var(--ds-fondo-l-item-line-height);
      --ds-fondo-description-font-size: var(--ds-fondo-l-description-font-size);
      --ds-fondo-description-line-height: var(--ds-fondo-l-description-line-height);
    }
  `],
})
export class DsFondoComponent {
  @Input() fondo: DsFondoValue = 'A';
  @Input() size: DsFondoSize = 'l';
  @Input() orientation: DsFondoOrientation = 'horizontal';
  @Input({ transform: booleanAttribute }) showDescription = true;
  @Input() description = '';

  get resolvedDescription(): string {
    return this.description || FONDO_DESCRIPTIONS[this.fondo];
  }

  get hostClasses(): string {
    return [
      'ds-fondo',
      `ds-fondo--${this.fondo}`,
      `ds-fondo--${this.size}`,
      `ds-fondo--${this.orientation}`,
    ].join(' ');
  }
}
